import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, CircularProgress, FormControlLabel, Grid2, Switch, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getCategories, getReferenceTables } from "../../api/reference";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../hooks/useFormStyles";
import { ReferenceTable } from "../../types/interfaces/admin";
import {
  Category,
  CreateVariableRequest,
  CreateVariableRequestSchema,
  UpdateVariableRequest,
  VariableResponse,
} from "../../types/model/variable";
import FormAutocomplete from "../General/FormAutocomplete";
import FormMultiAutocomplete from "../General/FormMultiAutocomplete";

interface VariableFormProps {
  variableData?: VariableResponse;
  onSubmit: (values: CreateVariableRequest | UpdateVariableRequest) => Promise<void>;
  onCancel?: () => void;
}

const VariableForm = ({
  variableData,
  onSubmit,
  onCancel,
}: VariableFormProps) => {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const isUpdateForm = !!variableData;
  const [isEditing, setIsEditing] = useState<boolean>(!isUpdateForm);
  const [isSaving, setIsSaving] = useState(false);
  const formStyles = useFormStyles(isEditing);
  const [categories, setCategories] = useState<Category[]>([]);
  const [referenceTables, setReferenceTables] = useState<{id: number, name: string}[]>([]);

  const methods = useForm<CreateVariableRequest | UpdateVariableRequest>({
    resolver: zodResolver(CreateVariableRequestSchema),
    defaultValues: {
      name: variableData?.name || "",
      unit: variableData?.unit || "",
      constraintText: variableData?.constraintText || "",
      isLookup: variableData?.isLookup || false,
      lookupTableId: variableData?.lookupTableId || undefined,
      categoryIds: variableData?.categories.map(c => c.id) || [],
    },
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    reset,
    setValue
  } = methods;

  const isLookup = watch("isLookup");

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cats = await getCategories();
        setCategories(cats);
      } catch (error) {
        handleError(error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchReferenceTables = async () => {
      try {
        const tables = await getReferenceTables();

        const filteredTables = tables.map((table: ReferenceTable) => ({
          id: table.id,
          name: table.tableName,
        }));

        setReferenceTables(filteredTables);
      } catch (error) {
        handleError(error);
      }
    };

    fetchReferenceTables();
  }, []);
  
  const handleFormSubmit = async (values: CreateVariableRequest | UpdateVariableRequest) => {
    try {
      setIsSaving(true);
      await onSubmit(values);
      if (isUpdateForm) setIsEditing(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <TextField
              {...register("name")}
              label={t("admin.variables.name")}
              fullWidth
              variant={isEditing ? "outlined" : "standard"}
              sx={{ ...formStyles }}
              error={!!errors.name}
              helperText={errors.name?.message}
              disabled={!isEditing || isSaving}
            />
          </Grid2>
          <Grid2 size={6}>
            <TextField
              {...register("unit")}
              label={t("admin.variables.unit")}
              fullWidth
              variant={isEditing ? "outlined" : "standard"}
              sx={{ ...formStyles }}
              error={!!errors.unit}
              helperText={errors.unit?.message}
              disabled={!isEditing || isSaving}
            />
          </Grid2>
          <Grid2 size={6} sx={{ alignSelf: 'flex-end' }}>
            <TextField
              {...register("constraintText")}
              label={t("admin.variables.constraint-text")}
              fullWidth
              variant={isEditing ? "outlined" : "standard"}
              sx={{ ...formStyles }}
              error={!!errors.constraintText}
              helperText={errors.constraintText?.message}
              disabled={!isEditing || isSaving}
            />
          </Grid2>
          <Grid2 size={6}>
            <FormMultiAutocomplete
              name="categoryIds"
              label={t("admin.variables.categories")}
              options={categories.map(c => ({id: c.id, name: t(c.i18nLabel)}))}
              isEditing={isEditing}
              isSaving={isSaving}
              formStyles={formStyles}
              disabled={!isEditing || isSaving}
            />
          </Grid2>
          <Grid2 size={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={isLookup}
                  onChange={(e) => setValue("isLookup", e.target.checked)}
                  color="primary"
                  disabled={!isEditing || isSaving}
                />
              }
              label={t("admin.variables.is-lookup")}
            />
          </Grid2>
          {isLookup && (
            <Grid2 size={6}>
              <FormAutocomplete
                name="lookupTableId" 
                label={t("admin.variables.lookup-table-id")} 
                options={referenceTables}
                isEditing={isEditing}
                isSaving={isSaving}
                disabled={false}
                formStyles={formStyles}
              />
            </Grid2>
          )}
        </Grid2>
        <Grid2
          size={12}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 3,
          }}
        >
          {isEditing ? (
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button type="submit" variant="contained" disabled={isSaving}>
                {isSaving ? <CircularProgress size={24} /> : t("generic.save")}
              </Button>
              {isUpdateForm ? (
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setIsEditing(false);
                    reset();
                  }}
                  disabled={isSaving}
                >
                  {t("generic.cancel")}
                </Button>
              ) : (
                onCancel && <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  onClick={onCancel}
                  disabled={isSaving}
                >
                  {t("generic.cancel")}
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                type="button"
                variant="contained"
                onClick={(e) => {
                  e.preventDefault();
                  setIsEditing(true);
                }}
              >
                {t("generic.edit")}
              </Button>
            </Box>
          )}
        </Grid2>
      </Box>
    </FormProvider>
  );
};

export default VariableForm;
